import { format, parseISO } from "date-fns";
import { CalendarIcon, LoaderIcon, TriangleAlert } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
	onlySeenConflicts,
	type RelocationCheck,
	type RescheduleRequest,
	rescheduleBounds,
	rescheduleRequest,
	sendJson,
	type TimelineEntry,
} from "@/lib/therapist-relocation";
import { cn } from "@/lib/utils";
import { ConflictList } from "./conflict-list";

// A 409's conflicts stay valid while the date is unchanged; the reason is not checked.
const keyOf = ({ effective_date }: RescheduleRequest) => effective_date;

/** Ubah tanggal: moves an Utama Mulai's start in one write to Go (BE-R2); the address stays. */
export function RescheduleRelocationDialog({
	entry,
	entries,
	onClose,
	onSaved,
	relocationsPath,
}: {
	/** null keeps the dialog closed */
	entry: TimelineEntry | null;
	entries: TimelineEntry[];
	onClose: () => void;
	/** Reloads what the write changed. */
	onSaved: () => void;
	/** The date change patches under it. */
	relocationsPath: string;
}) {
	const { t } = useTranslation("therapists", {
		keyPrefix: "relocation.reschedule",
	});
	const { locale, timezone } = useDateContext();
	const fieldId = useId();
	const day = (value: string) => format(parseISO(value), "PP", { locale });

	const [effectiveDate, setEffectiveDate] = useState("");
	const [reason, setReason] = useState("");
	const [calendarOpen, setCalendarOpen] = useState(false);
	const [check, setCheck] = useState<{
		key: string;
		data: RelocationCheck;
	} | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [attempted, setAttempted] = useState(false);
	const [saving, setSaving] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const outcomeRef = useRef<HTMLDivElement>(null);

	const bounds = entry
		? rescheduleBounds(new Date(), timezone, entries, entry)
		: { min: "", max: null, current: null };
	const parsed = rescheduleRequest({ effectiveDate, reason }, bounds);
	const issueAt = (path: string) =>
		parsed.error?.issues.some((issue) => issue.path.join(".") === path);
	const dateError = attempted && issueAt("effectiveDate");
	const reasonError = attempted && issueAt("reason");

	const fresh = parsed.success && check?.key === keyOf(parsed.data);
	const conflicts = fresh ? (check?.data.conflicts ?? []) : [];

	// Every opening starts from a blank form.
	useEffect(() => {
		if (!entry) return;
		setEffectiveDate("");
		setReason("");
		setCheck(null);
		setMessage(null);
		setAttempted(false);
		setConfirming(false);
	}, [entry]);

	useEffect(() => {
		if (message)
			outcomeRef.current?.scrollIntoView({
				block: "start",
				behavior: "smooth",
			});
	}, [message]);

	// The first save goes without acknowledge_conflicts and doubles as the conflict check. Go's
	// yes covers every conflict at save time, so it is only resent for visits already shown.
	const save = async () => {
		if (!entry) return;
		setAttempted(true);
		if (!parsed.success) return;
		const request = parsed.data;
		const url = `${relocationsPath}/${entry.therapistAddressId}/effective-date`;
		setMessage(null);
		setSaving(true);
		try {
			let result = await sendJson(
				url,
				{ ...request, acknowledge_conflicts: false },
				"PATCH",
			);
			if (
				result.status === 409 &&
				result.data &&
				conflicts.length > 0 &&
				onlySeenConflicts(result.data.conflicts, conflicts)
			) {
				result = await sendJson(
					url,
					{ ...request, acknowledge_conflicts: true },
					"PATCH",
				);
			}

			if (result.ok) {
				onClose();
				onSaved();
			} else if (result.status === 409 && result.data) {
				setCheck({ key: keyOf(request), data: result.data });
				setMessage(
					t(conflicts.length > 0 ? "errors.new_conflicts" : "errors.conflicts"),
				);
			} else {
				setMessage(result.error || t("errors.save_failed"));
			}
		} catch {
			setMessage(t("errors.save_failed"));
		} finally {
			setSaving(false);
			setConfirming(false);
		}
	};

	const requestSave = () => {
		if (conflicts.length > 0) setConfirming(true);
		else save();
	};

	return (
		<>
			<Dialog
				open={!!entry}
				onOpenChange={(next) => {
					if (!next && !saving) onClose();
				}}
			>
				<DialogContent className="flex max-h-[90dvh] flex-col gap-0 p-0 sm:max-w-2xl">
					<DialogHeader className="p-4 pb-3 border-b sm:p-6 sm:pb-4">
						<DialogTitle>{t("title")}</DialogTitle>
						<DialogDescription>{t("description")}</DialogDescription>
					</DialogHeader>

					{/* A fieldset cannot be the scroll box: as a flex item Chromium neither clips nor scrolls it. */}
					<div className="min-h-0 p-4 overflow-y-auto sm:p-6">
						<fieldset disabled={saving} className="grid min-w-0 gap-5">
							{entry && (
								<div className="grid gap-0.5 p-3 text-sm border bg-muted/40">
									<span className="font-semibold">{entry.address.city}</span>
									<span className="break-words">
										{entry.address.address}
										{entry.address.postalCode &&
											` · ${entry.address.postalCode}`}
									</span>
									{entry.effectiveFrom && (
										<span className="text-xs text-muted-foreground">
											{t("current_date", { date: day(entry.effectiveFrom) })}
										</span>
									)}
								</div>
							)}

							<div className="grid gap-1.5">
								<Label htmlFor={`${fieldId}-date`}>{t("date.label")}</Label>
								<Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
									<PopoverTrigger asChild>
										<Button
											id={`${fieldId}-date`}
											type="button"
											variant="outline"
											aria-invalid={dateError}
											className={cn(
												"justify-between w-full shadow-inner bg-input sm:w-72",
												!effectiveDate && "text-muted-foreground",
											)}
										>
											{effectiveDate
												? format(parseISO(effectiveDate), "PPPP", { locale })
												: t("date.placeholder")}
											<CalendarIcon className="opacity-75 size-4" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-auto p-0" align="start">
										{bounds.min && (
											<Calendar
												mode="single"
												locale={locale}
												selected={
													effectiveDate ? parseISO(effectiveDate) : undefined
												}
												defaultMonth={parseISO(
													effectiveDate || entry?.effectiveFrom || bounds.min,
												)}
												// Calendar days are local midnights, so formatting them keeps the day.
												disabled={(date) => {
													const value = format(date, "yyyy-MM-dd");
													return (
														value < bounds.min ||
														(!!bounds.max && value > bounds.max) ||
														value === bounds.current
													);
												}}
												onSelect={(date) => {
													if (!date) return;
													setEffectiveDate(format(date, "yyyy-MM-dd"));
													setCalendarOpen(false);
												}}
												initialFocus
											/>
										)}
									</PopoverContent>
								</Popover>
								<p
									className={cn(
										"text-xs",
										dateError ? "text-destructive" : "text-muted-foreground",
									)}
								>
									{bounds.min &&
										(bounds.max
											? t("date.hint_between", {
													min: day(bounds.min),
													max: day(bounds.max),
												})
											: t("date.hint", { date: day(bounds.min) }))}
								</p>
							</div>

							<div className="grid gap-1.5">
								<Label htmlFor={`${fieldId}-reason`}>{t("reason.label")}</Label>
								<Textarea
									id={`${fieldId}-reason`}
									value={reason}
									onChange={(event) => setReason(event.target.value)}
									placeholder={t("reason.placeholder")}
									aria-invalid={reasonError}
									className="shadow-inner bg-input"
								/>
								{reasonError && (
									<p role="alert" className="text-xs text-destructive">
										{t("errors.reason")}
									</p>
								)}
							</div>

							{/* The outcome lands below a long form; bring it into view whenever it changes. */}
							<div
								ref={outcomeRef}
								className="grid gap-5 empty:hidden scroll-mt-4"
							>
								{message && (
									<Alert variant="destructive">
										<TriangleAlert className="size-4" />
										<AlertDescription>{message}</AlertDescription>
									</Alert>
								)}

								{check && (
									<section
										aria-live="polite"
										className={cn("grid gap-2", !fresh && "opacity-50")}
									>
										<h3 className="text-sm font-semibold">
											{t("conflicts_title", {
												count: check.data.conflicts.length,
											})}
										</h3>
										<ConflictList conflicts={check.data.conflicts} />
									</section>
								)}
								{check && !fresh && (
									<p className="-mt-3 text-xs text-muted-foreground">
										{t("stale")}
									</p>
								)}
							</div>
						</fieldset>
					</div>

					<DialogFooter className="gap-2 p-4 border-t sm:p-6 sm:pt-4">
						<Button variant="outline" disabled={saving} onClick={onClose}>
							{t("cancel")}
						</Button>
						<Button disabled={saving} onClick={requestSave}>
							{saving && <LoaderIcon className="animate-spin" />}
							{t("save")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={confirming}
				onOpenChange={(next) => {
					if (!saving) setConfirming(next);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("confirm.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("confirm.description", { count: conflicts.length })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={saving}>
							{t("confirm.back")}
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={saving}
							onClick={(event) => {
								// keep the dialog open until the request finishes
								event.preventDefault();
								save();
							}}
						>
							{saving && <LoaderIcon className="animate-spin" />}
							{t("confirm.save")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
